import { classMap } from 'lit/directives/class-map.js'
import { ifDefined } from 'lit/directives/if-defined.js'
import { property } from 'lit/decorators.js'
import { ref } from 'lit/directives/ref.js'
import { html, nothing } from 'lit'

import { ariaElements, delegateEvents, refCollection } from '#utils/directives'
import { boolean } from '../form-boolean/lfds-boolean.css.ts'
import styles from '../form-boolean-card/lfds-boolean-card.css.ts'
import LfdsIcon from '#components/icon/icon/lfds-icon.ts'
import { LfuiElement } from '#utils/element'
import {
	WithInternals,
	WithFocus,
	internals,
	compose,
	anchor
} from '#utils/mixins'

/**
 * @name LfdsCheckboxCard
 * @tag lfds-checkbox-card
 * @status deprecated
 */
export default class LfdsCheckboxCard extends compose(
	LfuiElement,
	WithInternals<HTMLInputElement>,
	WithFocus
) {
	static styles = [LfuiElement.styles, styles, boolean]
	static events = ['change']

	protected delegatedEvents = ['change', 'focus', 'blur', 'focusin', 'focusout']

	@property() label!: string
	@property() name: string
	@property() value: string
	@property() description: string
	@property({ attribute: 'icon-id' }) iconId: string
	@property() layout: 'auto' | 'vertical' | 'horizontal' = 'auto'
	@property() autocomplete: 'on' | 'off'
	@property({ type: Boolean }) indeterminate: boolean = false
	@property({ type: Boolean }) checked: boolean = false
	@property({ type: Boolean }) required: boolean
	@property({ type: Boolean }) disabled: boolean
	@property({ type: Boolean }) invalid: boolean
	@property({ attribute: 'validation-message' }) validationMessage: string
	@property({ attribute: 'sr-label' }) srLabel: string
	@property({ attribute: 'sr-labelledby' }) srLabelledby: string
	@property({ attribute: 'sr-describedby' }) srDescribedby: string
	@property({ attribute: 'sr-controls' }) srControls: string

	#labels = refCollection()
	#descriptions = refCollection()

	constructor() {
		super()
		LfdsIcon.preload('alert', 24)
	}

	connectedCallback() {
		super.connectedCallback()
		if (!this.hasAttribute('role')) this.setAttribute('role', 'none')
		this[internals].form?.addEventListener('reset', () => {
			this.checked = this.hasAttribute('checked')
			this.invalid = false
		})
		this.addEventListener('invalid', () => {
			this.invalid = true
		})
		this.addEventListener('click', this.#handleHostClick)
	}

	disconnectedCallback() {
		super.disconnectedCallback()
		this.removeEventListener('click', this.#handleHostClick)
	}

	#handleHostClick = (event: Event) => {
		if (event.composedPath()[0] === this) {
			this[anchor].value?.click()
		}
	}

	updated(changed) {
		super.updated(changed)
		if (this.checked) {
			const data = new FormData()
			data.set(this.name, this.value ?? 'on')
			this[internals].setFormValue(data)
		} else {
			this[internals].setFormValue(null)
		}
	}

	#handleChange = (event: Event) => {
		this.checked = (event.target as HTMLInputElement).checked
		this.indeterminate = false
		this.invalid = false
	}

	render() {
		return html`
			<div
				class=${classMap({
					card: true,
					vertical: this.layout === 'vertical',
					horizontal: this.layout === 'horizontal',
					'focus-visible': this.focusVisible
				})}
			>
				<div class="control ${this.checked ? 'checked' : ''}">
					<input
						id="input"
						type="checkbox"
						class="boolean checkbox"
						aria-invalid=${this.invalid ? 'true' : (nothing as any)}
						aria-label=${ifDefined(this.srLabel)}
						value=${ifDefined(this.value)}
						name=${ifDefined(this.name)}
						?required=${this.required}
						?disabled=${this.disabled}
						?invalid=${this.invalid}
						.checked=${this.checked}
						.indeterminate=${this.indeterminate}
						${ref(this[anchor])}
						${delegateEvents(this.delegatedEvents, {
							before: { change: this.#handleChange }
						})}
						${this.#labels.bind('ariaLabelledByElements')}
						${this.#descriptions.bind('ariaDescribedByElements')}
						${ariaElements({
							describedBy: this.srDescribedby,
							labelledBy: this.srLabelledby,
							controls: this.srControls
						})}
					/>
				</div>
				<label class="label" for="input" ${this.#labels.add()}>
					<slot name="label">${this.label}</slot>
				</label>
				<p class="description" ${this.#descriptions.add()}>
					<slot name="description">${this.description}</slot>
				</p>
				<span class="figure">
					<slot name="icon">
						${this.iconId
							? html`<lfds-icon icon-id=${this.iconId} size="40"></lfds-icon>`
							: nothing}
					</slot>
				</span>
			</div>
			${this.validationMessage
				? html`
						<div aria-atomic="true" role="alert" class="validation">
							<span class="icon">
								<lfds-icon
									icon-id="alert"
									size="24"
									color="currentcolor"
								></lfds-icon>
							</span>
							<span ${this.#descriptions.add()}>${this.validationMessage}</span>
						</div>
					`
				: nothing}
		`
	}
}
